import { classMerge } from '@components/utilities/classMerge'
import { handleKeyboardAction } from '@components/utilities/handleKeyboardAction'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type {
  BackgroundColor,
  BorderColor,
  TextColor,
} from '@lib/colors/colors'
import type { ReactNode } from 'react'
import { Icon, type SizeProp } from '../Icon'

export default function Tab({
  alignment = 'horizontal',
  className = 'text-sm',
  disabled,
  extraElem,
  icon,
  iconClassName,
  iconPosition,
  iconSize,
  label,
  onClick,
  oppositeExtraElem,
  selected,
  selectedBackgroundColor,
  selectedBorderColor = 'border-blue500',
  selectedFontColor = 'text-blue500',
}: {
  alignment?: 'horizontal' | 'vertical'
  className?: string
  disabled?: boolean
  extraElem?: ReactNode
  icon?: IconDefinition
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  iconSize?: SizeProp
  label: string
  onClick: () => void
  oppositeExtraElem?: ReactNode
  selected: boolean
  selectedBackgroundColor?: BackgroundColor
  selectedBorderColor?: BorderColor
  selectedFontColor?: TextColor
}) {
  const isHorizontal = alignment === 'horizontal'

  const handleClick = () => {
    if (!disabled) {
      onClick()
    }
  }

  return (
    <div
      aria-disabled={disabled}
      aria-label={label}
      aria-selected={selected}
      className={classMerge(
        'group relative z-10 flex cursor-pointer select-none content-center border-transparent p-4',
        isHorizontal ? 'border-b-2' : 'border-l-2',
        selected && [selectedBackgroundColor, selectedBorderColor],
        disabled ? 'cursor-not-allowed' : '',
        className,
      )}
      onClick={handleClick}
      onKeyDown={(e) => handleKeyboardAction(e, handleClick)}
      role='tab'
      tabIndex={disabled ? -1 : 0}
    >
      <div
        className={classMerge('flex items-center justify-center gap-1.5', {
          'flex-row-reverse': isHorizontal && iconPosition === 'left',
        })}
      >
        {oppositeExtraElem}
        <span
          className={classMerge(
            selected ? selectedFontColor : 'text-neutral600',
          )}
        >
          {label}
        </span>
        {icon && <Icon className={iconClassName} icon={icon} size={iconSize} />}
        {extraElem}
      </div>
    </div>
  )
}
